import {Component, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {BehaviorSubject, lastValueFrom, Observable, of} from 'rxjs';
import {AdminContextSelectorService} from './admin-context-selector.service';
import {
  AdminContextService,
  Landlord,
  Location,
  Offering,
  SelectedAdminContext
} from "@app/security/admin-context.service";
import {MaterialModule} from "@ui-webkit/modules/material.module";
import {LoadingService} from "@ui-kit/utils/service/loading.service";
import {MatAutocompleteSelectedEvent} from "@angular/material/autocomplete";
import {MatDialog} from '@angular/material/dialog';
import {CreateLandlordDialogComponent} from '../../../features/onboarding/setup/create-landlord-dialog/create-landlord-dialog.component';
import {CreateLocationDialogComponent} from '../../../features/onboarding/setup/create-location-dialog/create-location-dialog.component';
import {CreateOfferingDialogComponent} from '../../../features/onboarding/setup/create-offering-dialog/create-offering-dialog.component';

@Component({
  selector: 'app-admin-context-selector',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialModule,
    CreateLandlordDialogComponent,
    CreateLocationDialogComponent,
    CreateOfferingDialogComponent,
  ],
  templateUrl: './admin-context-selector.component.html',
  styleUrls: ['./admin-context-selector.component.scss']
})
export class AdminContextSelectorComponent implements OnInit {
  landlords: Landlord[] = [];
  landlordCtrl = new FormControl();
  locationCtrl = new FormControl();
  offeringCtrl = new FormControl();

  filteredLandlords!: Observable<Landlord[]>;
  filteredLocations!: Observable<Location[]>;
  filteredOfferings!: Observable<Offering[]>;

  private _loading = new BehaviorSubject<boolean>(false);
  protected loading$ = this._loading.asObservable();

  constructor(private _service: AdminContextSelectorService,
              private _adminContext: AdminContextService,
              protected siteLoader: LoadingService,
              private _dialog: MatDialog) {

    siteLoader.loading$.subscribe(loading => {
      this._loading.next(loading);
    })

    this.loading$.subscribe(loading => {
      if (loading) {
        this.landlordCtrl.disable();
        this.locationCtrl.disable();
        this.offeringCtrl.disable();
      } else {
        this.landlordCtrl.enable();
        this.locationCtrl.enable();
        this.offeringCtrl.enable();
      }
    })
  }

  get selectedContext(): SelectedAdminContext {
    return this._adminContext.selectedAdminContext;
  }

  ngOnInit(): void {
    this.loadLandlords();
  }

  loadLandlords(clearContext = false,
                select?: { landlordUuid?: string, locationUuid?: string, offeringUuid?: string }): void {
    this._loading.next(true);
    if (clearContext) {
      this.clearLandlords();
    }

    lastValueFrom(this._service.getLandlords(clearContext))
      .then(landlords => {
        this.landlords = landlords;
        if (select?.landlordUuid) {
          this.applySelection(select.landlordUuid, select.locationUuid, select.offeringUuid);
        } else {
          this.initializeFromContext();
        }
        return Promise.resolve();
      })
      .finally(() => {
        this._loading.next(false);
      });
  }

  /**
   * Switch context straight to a just-created landlord / location / offering,
   * drilling only as deep as the provided uuids resolve in the freshly loaded
   * hierarchy. Refreshes the dropdown option lists so each is visible, not just
   * selected.
   */
  private applySelection(landlordUuid: string, locationUuid?: string, offeringUuid?: string): void {
    const landlord = this.landlords.find(l => l.uuid?.toLowerCase() === landlordUuid.toLowerCase());
    if (!landlord) {
      this.initializeFromContext();
      return;
    }
    this.filteredLandlords = of(this.landlords);
    this.landlordCtrl.setValue(landlord);
    this.updateLandlords(landlord);

    const location = locationUuid
      ? landlord.locations.find(loc => loc.uuid?.toLowerCase() === locationUuid.toLowerCase())
      : undefined;
    if (!location) {
      return;
    }
    this.locationCtrl.setValue(location);
    this.updateLocations(location);

    const offering = offeringUuid
      ? location.offerings.find(o => o.uuid?.toLowerCase() === offeringUuid.toLowerCase())
      : undefined;
    if (!offering) {
      return;
    }
    this.offeringCtrl.setValue(offering);
    this.updateOfferings(offering);
  }

  /**
   * Restores the dropdowns from the stored selection after a page refresh. The uuid
   * comparisons are case-insensitive, matching every other uuid comparison in this
   * component: an exact compare here dropped the whole selection (via
   * {@link clearLandlords}) whenever a stored uuid differed only in case from the one
   * the API returned — part of TF-6.
   */
  private initializeFromContext() {
    const selectedLandlordUuid = this._adminContext.selectedLandlordUuid;
    const selectedLocationUuid = this._adminContext.selectedLocationUuid;
    const selectedOfferingUuid = this._adminContext.selectedOfferingUuid;

    if (selectedLandlordUuid) {
      const landlord = this.landlords.find(landlord => this.sameUuid(landlord.uuid, selectedLandlordUuid))
      if (!landlord) {
        this.clearLandlords();
      } else {
        this.landlordCtrl.setValue(landlord);

        if (selectedLocationUuid) {
          const location = landlord?.locations.find(location => this.sameUuid(location.uuid, selectedLocationUuid));
          if (!location) {
            this.clearLocations();
          } else {
            this.locationCtrl.setValue(location);

            if (selectedOfferingUuid) {
              const offering = location.offerings.find(offering => this.sameUuid(offering.uuid, selectedOfferingUuid));
              if (!offering) {
                this.clearOfferings();
              } else {
                this.offeringCtrl.setValue(offering);
              }
            }
          }
        }
      }
    } else {
      this.clearLandlords();
    }
  }

  private sameUuid(a: string | null | undefined, b: string | null | undefined): boolean {
    return !!a && !!b && a.toLowerCase() === b.toLowerCase();
  }

  displayFn(item: Landlord | Location | Offering): string {
    return item ? item?.name : '';
  }

  clearLandlords(): void {
    // Clearing the field means "forget the selection", not "reset the session" —
    // say so explicitly now that reset() leaves the stored selection alone (TF-6).
    this._adminContext.resetSelectedContext();
    this.landlordCtrl.reset();
    this.locationCtrl.reset();
    this.offeringCtrl.reset();

    this.filteredLandlords = of(this.landlords);
    this.filteredLocations = of([]);
    this.filteredOfferings = of([]);
  }

  updateLandlords(selected: Landlord | string | null): void {
    if (typeof selected !== 'string') {
      this.filteredLocations = of(selected ? selected.locations : []);

      const found = selected?.locations.find(f => f.uuid?.toLowerCase() === this.locationCtrl.value?.uuid?.toLowerCase());

      if (!found || this._adminContext.selectedAdminContext.landlord?.uuid !== selected?.uuid) {
        this._adminContext.update({landlord: selected, location: null, offering: null});
        this.clearLocations();
      }
    }
  }

  clearLocations(): void {
    this._adminContext.update({location: null, offering: null});
    this.locationCtrl.reset();
    this.offeringCtrl.reset();

    if (this._adminContext.selectedAdminContext.landlord) {
      this.filteredLocations = of(this._adminContext.selectedAdminContext.landlord.locations);
    }
    this.filteredOfferings = of([]);
  }

  updateLocations(selected: Location | string | null): void {
    if (typeof selected !== 'string') {
      this.filteredOfferings = of(selected ? selected.offerings : []);

      const found = selected?.offerings.find(f => f.uuid?.toLowerCase() === this.offeringCtrl.value?.uuid?.toLowerCase());

      if (!found || this._adminContext.selectedAdminContext.location?.uuid !== selected?.uuid) {
        this._adminContext.update({location: selected, offering: null});
        this.clearOfferings();
      }
    }
  }

  clearOfferings(): void {
    this._adminContext.update({offering: null});
    this.offeringCtrl.reset();

    if (this._adminContext.selectedAdminContext.location) {
      this.filteredOfferings = of(this._adminContext.selectedAdminContext.location.offerings);
    }
  }

  updateOfferings(selected: Offering | string | null): void {
    if (typeof selected !== 'string') {
      this._adminContext.update({offering: selected});

      const found = this._adminContext.selectedAdminContext.location?.offerings.find(f => f.uuid?.toLowerCase() === selected?.uuid?.toLowerCase());
      if (!found) {
        this.offeringCtrl.reset();
        this.filteredOfferings = of(this._adminContext.selectedAdminContext?.location?.offerings ?? []);
      }
    }
  }

  protected readonly ADD_LANDLORD_SENTINEL = '__ADD_LANDLORD__';
  protected readonly ADD_LOCATION_SENTINEL = '__ADD_LOCATION__';
  protected readonly ADD_OFFERING_SENTINEL = '__ADD_OFFERING__';

  onLandlordChange($event: MatAutocompleteSelectedEvent) {
    if ($event.option.value === this.ADD_LANDLORD_SENTINEL) {
      this.landlordCtrl.reset();
      this.filteredLandlords = of(this.landlords);
      this.openCreateLandlord();
      return;
    }
    this.updateLandlords($event.option.value);
  }

  onLocationChange($event: MatAutocompleteSelectedEvent) {
    if ($event.option.value === this.ADD_LOCATION_SENTINEL) {
      this.locationCtrl.reset();
      this.filteredLocations = of(this._adminContext.selectedAdminContext.landlord?.locations ?? []);
      this.openCreateLocation();
      return;
    }
    this.updateLocations($event.option.value);
  }

  onOfferingChange($event: MatAutocompleteSelectedEvent) {
    if ($event.option.value === this.ADD_OFFERING_SENTINEL) {
      this.offeringCtrl.reset();
      this.filteredOfferings = of(this._adminContext.selectedAdminContext.location?.offerings ?? []);
      this.openCreateOffering();
      return;
    }
    this.updateOfferings($event.option.value);
  }

  openCreateLandlord(): void {
    this._dialog.open(CreateLandlordDialogComponent, {minWidth: '640px'})
      .afterClosed().subscribe(uuid => { if (uuid) this.loadLandlords(true, {landlordUuid: uuid}); });
  }

  openCreateLocation(): void {
    const landlordUuid = this._adminContext.selectedLandlordUuid ?? undefined;
    this._dialog.open(CreateLocationDialogComponent, {
      minWidth: '560px',
      data: {landlordUuid}
    }).afterClosed().subscribe(uuid => {
      if (uuid) this.loadLandlords(true, {landlordUuid, locationUuid: uuid});
    });
  }

  openCreateOffering(): void {
    const landlordUuid = this._adminContext.selectedLandlordUuid ?? undefined;
    const locationUuid = this._adminContext.selectedLocationUuid ?? undefined;
    this._dialog.open(CreateOfferingDialogComponent, {
      minWidth: '640px',
      data: {locationUuid}
    }).afterClosed().subscribe(uuid => {
      if (uuid) this.loadLandlords(true, {landlordUuid, locationUuid, offeringUuid: uuid});
    });
  }

}
